import { AvatarSizes, type TranslationIDs, useIntl } from '@capsicum/common';
import { Text, Tooltip } from '@capsicum/common-web';
import { ApprovedFooter, HeaderWarning } from '@capsicum/features/regulations/web';
import { type ReactNode, useCallback } from 'react';

import * as S from './Tutorials.styled';

import type { WCPledgeTutorialSteps } from '@capsicum/features/world-championship-pledge/shared';

export type TutorialStepProps = {
	goToStep: (index: number) => void;
	header?: TranslationIDs;
	numberOfSteps: number;
	stepId: WCPledgeTutorialSteps;
	stepNumber: number;
	text: TranslationIDs;
	textParams?: Record<string, unknown>;
	tooltipText?: TranslationIDs;
};

export const TutorialStep = ({
	goToStep,
	header,
	numberOfSteps,
	stepId,
	stepNumber,
	text,
	textParams,
	tooltipText
}: TutorialStepProps) => {
	const { $t } = useIntl();

	const renderStepsNavigation = useCallback((currentStep: number) => {
		return Array.from({ length: numberOfSteps }, (_, index) => {
			const stepIndex = index + 1;
			const isActive = currentStep >= stepIndex;

			return (
				<S.StepBallWrapper key={stepIndex} onClick={() => goToStep(stepIndex)}>
					<S.StepBall $active={isActive} />
				</S.StepBallWrapper>
			);
		});
	}, []);

	return (
		<S.Container>
			<HeaderWarning page="wcPledge" />
			<S.HeaderContainer>
				<S.ImageContainer>
					<S.StyledImage
						image={`wCPledgeTutorial3DImage${stepId}`}
						imageStyle={{ objectFit: 'contain' }}
						testID={`wc-pledge-tutorial${stepId}-image`}
					/>
				</S.ImageContainer>
				<S.StepsNavigation>{renderStepsNavigation(stepNumber)}</S.StepsNavigation>
			</S.HeaderContainer>
			<S.BodyContainer>
				<S.TitleContainer>
					{header && <S.Title variant="display2">{$t(header)}</S.Title>}
					<S.StepText testID={`wc-pledge-tutorial${stepId}-title`} variant="title4">
						{$t(text, {
							highlight: (chunks: Array<ReactNode>) => <S.Highlighted>{chunks}</S.Highlighted>,
							...textParams
						})}
						{tooltipText && (
							<S.TooltipWrapper>
								<Tooltip>
									<Tooltip.Trigger
										size={AvatarSizes.XXXS}
										testID={`wc-pledge-tutorial${stepId}-tooltip`}
									/>
									<Tooltip.Content>
										<Tooltip.Description>
											<Text
												testID={`wc-pledge-tutorial${stepId}-tooltip-description`}
												variant="text5"
											>
												{$t(tooltipText)}
											</Text>
										</Tooltip.Description>
									</Tooltip.Content>
								</Tooltip>
							</S.TooltipWrapper>
						)}
					</S.StepText>
				</S.TitleContainer>
				<ApprovedFooter page="wcPledge" />
			</S.BodyContainer>
		</S.Container>
	);
};
